/**
 * DoctorStatistics Component
 * 
 * This React component displays a comprehensive report of doctor performance statistics.
 * It fetches data from the backend API and presents it in a paginated table format.
 * 
 * Features:
 * - Fetches live doctor statistics from the database
 * - Displays script counts, revenue, discounts, and income per doctor
 * - Supports pagination for large datasets
 * - Includes functional date filtering capabilities
 * - Shows loading states during data fetching
 * - Handles error states gracefully
 * - Automatically refreshes data when date filters change
 */

import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../../../../../components/navbar.jsx';
import Footer from '../../../../../components/footer.jsx';
import Button from '../../../../../components/Button.jsx';
import Pagination from '../../../../../components/Pagination.jsx';
import DatePicker from '../../../../../components/DatePicker.jsx';
import { ReportsSidebar, ReportsTable } from '../../components/index.js';
import { doctorStatsApi } from '../../../../../api/dispensary/reports/DoctorStats.js/doctorstatsApi.js';
import { getBranchByCode } from '../../../../../api/branchManagement]/branchApi.js';
import { useAuth } from '../../../../../context/AuthContext.jsx';
import PrintReport from '../../components/PrintReport.jsx';
import styles from '../css/DoctorStatistics.module.css';

const DoctorStatistics = () => {
  // Get user authentication data
  const { user } = useAuth();
  
  // State management for component data and UI
  const [fromDate, setFromDate] = useState(null);  // Date filter start (UI only)
  const [toDate, setToDate] = useState(null);      // Date filter end (UI only)
  const [currentPage, setCurrentPage] = useState(1); // Current pagination page
  const [doctorStats, setDoctorStats] = useState([]); // Doctor statistics data
  const [loading, setLoading] = useState(true);     // Loading state
  const [error, setError] = useState(null);         // Error state
  const [branchInfo, setBranchInfo] = useState(null); // Branch information

  // Max date set to today to prevent future date selection
  const maxDate = new Date();

  /**
   * useEffect hook to fetch branch information when component mounts
   * This runs once on component load to get pharmacy details for PDF export
   */
  useEffect(() => {
    /**
     * Async function to fetch branch information from the backend API
     * Uses the user's branchcode to get specific branch details
     */
    const fetchBranchInfo = async () => {
      try {
        if (user?.branchcode) {
          const response = await getBranchByCode(user.branchcode);
          if (response.success) {
            setBranchInfo(response.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch branch info:', error);
        // Set default branch info if API fails
        setBranchInfo({
          branchname: 'DURBAN NORTH PHARMACY',
          address: '123 Umhlanga Rocks Drive, Durban North, 4051',
          telephoneno: '(031) 123-4567'
        });
      }
    };

    // Execute the fetch function
    fetchBranchInfo();
  }, [user?.branchcode]);

  /**
   * useEffect hook to fetch doctor statistics when component mounts or dates change
   * This runs once on component load and whenever fromDate or toDate changes
   */
  useEffect(() => {
    /**
     * Async function to fetch doctor statistics from the backend API
     * Handles loading states, data processing, and error handling
     * Accepts optional date parameters for filtering
     */
    const fetchDoctorStats = async () => {
      try {
        // Set loading state to true to show loading indicator
        setLoading(true);
        
        // Format dates for API (convert to ISO string format)
        const formattedFromDate = fromDate ? fromDate.toISOString().split('T')[0] : null;
        const formattedToDate = toDate ? toDate.toISOString().split('T')[0] : null;
        
        // Make API call to fetch doctor statistics with date filters
        const response = await doctorStatsApi.getDoctorStats(formattedFromDate, formattedToDate);
        
        // Check if API response was successful and contains data
        if (response.success) {
          // Store the doctor statistics data in component state
          console.log('Backend doctor stats data:', response.data);
          setDoctorStats(response.data);
        } else {
          console.log('API response not successful:', response);
        }
      } catch (error) {
        // Log any errors that occur during API call
        console.error('Failed to fetch doctor stats:', error);
      } finally {
        // Always set loading to false when operation completes
        setLoading(false);
      }
    };

    // Execute the fetch function
    fetchDoctorStats();
  }, [fromDate, toDate]); // Dependency array includes dates to refetch when they change

  /**
   * useMemo hook to optimize performance
   * Creates a memoized version of the doctorStats array
   * Only recalculates when doctorStats data changes
   */
  const rows = useMemo(() => {
    // Always use backend data (empty array if no data yet)
    console.log('Using backend data:', doctorStats);
    return doctorStats || [];
  }, [doctorStats]);

  // Pagination calculations
  const rowsPerPage = 5; // Number of rows to display per page
  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage)); // Total pages needed
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages); // Ensure current page is valid
  const startIndex = (safeCurrentPage - 1) * rowsPerPage; // Starting index for current page
  const endIndex = startIndex + rowsPerPage; // Ending index for current page
  const pageRows = rows.slice(startIndex, endIndex); // Rows to display on current page

  /**
   * Utility function to safely convert values to numbers
   * Handles string numbers with commas and null/undefined values
   * 
   * @param {*} val - Value to convert to number
   * @returns {number} - Safely converted number or 0 if invalid
   */
  const toNumber = (val) => {
    const n = Number(String(val ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Utility function to format numbers to 2 decimal places
   * Used for displaying monetary values consistently
   * 
   * @param {number} n - Number to format
   * @returns {string} - Number formatted to 2 decimal places
   */
  const format2 = (n) => Number(n).toFixed(2);

  const totalsRow = useMemo(
    () => ({
      isTotal: true,
      doctor: 'Totals',
      scripts: format2(rows.reduce((s, r) => s + toNumber(r.scripts), 0)),
      gross: format2(rows.reduce((s, r) => s + toNumber(r.gross), 0)),
      disc: format2(rows.reduce((s, r) => s + toNumber(r.disc), 0)),
      admin: format2(rows.reduce((s, r) => s + toNumber(r.admin), 0)),
      income: format2(rows.reduce((s, r) => s + toNumber(r.income), 0)),
    }),
    [rows]
  );

  const tableData = safeCurrentPage === totalPages ? [...pageRows, totalsRow] : pageRows;

  const columns = useMemo(
    () => [
      { header: 'Doctor', accessor: 'doctor' },
      { header: 'Scripts', accessor: 'scripts' },
      { header: 'Gross', accessor: 'gross' },
      { header: '-Disc', accessor: 'disc' },
      { header: '-Admin', accessor: 'admin' },
      { header: 'Income', accessor: 'income' },
    ],
    []
  );

  const handlePrint = () => {
    PrintReport({
      reportType: 'doctor',
      reportTitle: 'DOCTOR STATISTICS REPORT',
      branchInfo,
      headers: ['Doctor Name', 'Scripts', 'Gross', 'Income', 'Cost', 'Ave.Income'],
      data: rows, // Use all rows for print (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: true
    });
  };

  const handleExport = () => {
    PrintReport({
      reportType: 'doctor',
      reportTitle: 'DOCTOR STATISTICS REPORT',
      branchInfo,
      headers: ['Doctor Name', 'Scripts', 'Gross', 'Income', 'Cost', 'Ave.Income'],
      data: rows, // Use all rows for export (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: false
    });
  };

 return (
  <div className={styles.container}>
    <Navbar backTo="/customer/dispensary" backReplace />

    <div className={styles.mainContent}>
      <div className={styles.layout}>
        <div className={styles.sidebarContainer}>
          <ReportsSidebar />
        </div>

        <div className={styles.content}>
          <div className={styles.reportContainer}>

            {/* DATE FILTERS */}
            <div className={styles.dateFiltersSection}>
              <div className={styles.dateFilters}>
                <div className={styles.dateFilter}>
                  <label className={styles.dateLabel}>From</label>
                  <DatePicker
                    selectedDate={fromDate}
                    onDateChange={setFromDate}
                    className={styles.dateInput}
                    maxDate={maxDate}
                    disableFutureDates={true}
                  />
                </div>

                <div className={styles.dateFilter}>
                  <label className={styles.dateLabel}>To</label>
                  <DatePicker
                    selectedDate={toDate}
                    onDateChange={setToDate}
                    className={styles.dateInput}
                    maxDate={maxDate}
                    disableFutureDates={true}
                  />
                </div>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className={styles.actionButtonsSection}>
              <div className={styles.actionButtons}>
                <Button onClick={handlePrint} variant="approve" className={styles.printButton}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9V2h12v7" />
                    <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" />
                  </svg>
                  Print
                </Button>

                <Button onClick={handleExport} variant="primary" className={styles.exportButton}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Export
                </Button>
              </div>
            </div>

            {/* NOTICE */}
            <div className={styles.noticeRow}>
              <span className={styles.noticeText}>
                P.O.S Discount Not Taken Into Account!
              </span>
            </div>

            {/* TABLE */}
            <div className={styles.tableWrapper}>
              {loading ? (
                <div className={styles.loadingContainer}>
                  Loading doctor statistics...
                </div>
              ) : (
                <ReportsTable columns={columns} data={tableData} striped />
              )}
            </div>

            {/* PAGINATION */}
            <div className={styles.paginationContainer}>
              <Pagination
                currentPage={safeCurrentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>

          </div> {/* reportContainer */}
        </div> {/* content */}
      </div> {/* layout */}
    </div> {/* mainContent */}

    <Footer />

  </div>
)};

export default DoctorStatistics;
